import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownWideShort,
  faArrowUpShortWide,
  faChevronsRight,
} from '@fortawesome/pro-regular-svg-icons';
import { toast } from 'react-toastify';
import { TimesheetSubmissionFrequency } from '@core/coreLoopConfiguration/coreLoopConfiguration.types';
import { DaySequenceChangeInput } from '@core/generated/gql-types';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import Button from '@/components/Button';
import ApproveTimesheetsModal from '@/modules/timesheets/components/ApproveTimesheetsModal';
import TimesheetCard from '@/modules/timesheets/components/TimesheetCard';
import TimesheetDrawerParties from '@/modules/timesheets/components/TimesheetDrawerParties';
import {
  formatTimesheetDateRange,
  groupTimesheetsByPayCycle,
  type PayPeriod,
  type TimesheetGroup,
  type TimesheetPayCycleGroup,
  type TimesheetTimeSort,
} from '@/modules/timesheets/utils/timesheetGroups.utils';

interface TimesheetGroupDrawerProps {
  group: TimesheetGroup | null;
  /** The pay period selected on the page, shown in the heading. */
  payPeriod: PayPeriod;
  isOpen: boolean;
  canApprove: boolean;
  showClientName: boolean;
  /** Pay cycle from the tenant core loop. Missing means a daily cycle. */
  payCycleFrequency?: TimesheetSubmissionFrequency | null;
  /** Pay-run anchor from the tenant core loop. Missing uses the default. */
  payRunCycleAnchorDate?: string | null;
  /** Key of the cycle currently being approved, if any. */
  approvingCycleKey: string | null;
  onClose: () => void;
  onApproveCycle: (cycleKey: string, daySequenceIds: string[]) => Promise<void>;
  onTimesheetSaved: () => Promise<void>;
}

const TimesheetGroupDrawer = ({
  group,
  payPeriod,
  isOpen,
  canApprove,
  showClientName,
  payCycleFrequency,
  payRunCycleAnchorDate,
  approvingCycleKey,
  onClose,
  onApproveCycle,
  onTimesheetSaved,
}: TimesheetGroupDrawerProps) => {
  const proposeDaySequenceChanges = useDaySequenceStore(
    (state) => state.proposeDaySequenceChanges,
  );
  const [editingDaySequenceId, setEditingDaySequenceId] = useState<
    string | null
  >(null);
  const [savingDaySequenceId, setSavingDaySequenceId] = useState<string | null>(
    null,
  );
  const [payCycleToApprove, setPayCycleToApprove] =
    useState<TimesheetPayCycleGroup | null>(null);
  const [timeSort, setTimeSort] = useState<TimesheetTimeSort>('asc');
  const payCycles = useMemo(
    () =>
      groupTimesheetsByPayCycle(
        group?.items ?? [],
        { frequency: payCycleFrequency, anchorDate: payRunCycleAnchorDate },
        timeSort,
      ),
    [group?.items, payCycleFrequency, payRunCycleAnchorDate, timeSort],
  );

  useEffect(() => {
    setEditingDaySequenceId(null);
    setSavingDaySequenceId(null);
    setPayCycleToApprove(null);
    setTimeSort('asc');
  }, [group?.rowKey, isOpen]);

  const saveTimesheet = async (change: DaySequenceChangeInput) => {
    setSavingDaySequenceId(change.id);
    try {
      const updated = await proposeDaySequenceChanges([change]);
      if (updated.length === 0) {
        toast.error('Failed to propose the timesheet change');
        return;
      }
      toast.success('Timesheet change proposed');
      setEditingDaySequenceId(null);
      await onTimesheetSaved();
    } catch (error) {
      console.error('Error proposing timesheet change:', error);
      toast.error('Error while proposing the timesheet change');
    } finally {
      setSavingDaySequenceId(null);
    }
  };

  return (
    <>
      <Transition show={isOpen && !!group} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/5" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={React.Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    {group && (
                      <div className="flex h-full flex-col bg-gray-50 shadow-xl">
                        <div className="border-b border-gray-200 bg-white px-6 py-4">
                          <button
                            type="button"
                            aria-label="Close"
                            className="cursor-pointer rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                            onClick={onClose}
                          >
                            <FontAwesomeIcon
                              icon={faChevronsRight}
                              className="ml-1 h-3 w-3"
                            />
                          </button>
                          <TimesheetDrawerParties
                            userId={group.userId}
                            workerName={group.workerName}
                            workerProfileImageUrl={group.workerProfileImageUrl}
                            clientId={group.clientId}
                            clientName={group.clientName}
                            showClientName={showClientName}
                          />
                          <div className="mt-2 flex items-center justify-between gap-3">
                            <Dialog.Title className="text-sm font-semibold text-gray-900">
                              Timesheets for{' '}
                              {formatTimesheetDateRange(
                                payPeriod.startDateISO,
                                payPeriod.endDateISO,
                              )}
                            </Dialog.Title>
                            <button
                              type="button"
                              className={`inline-flex shrink-0 items-center gap-1.5 text-sm font-medium ${actionLinkClassName}`}
                              onClick={() =>
                                setTimeSort((current) =>
                                  current === 'desc' ? 'asc' : 'desc',
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  timeSort === 'desc'
                                    ? faArrowDownWideShort
                                    : faArrowUpShortWide
                                }
                                className="h-3.5 w-3.5"
                              />
                              {timeSort === 'desc'
                                ? 'Newest first'
                                : 'Oldest first'}
                            </button>
                          </div>
                        </div>

                        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
                          {payCycles.map((payCycle) => (
                            <section key={payCycle.key} className="space-y-3">
                              <h3 className="text-sm font-semibold text-gray-900">
                                {payCycle.key === 'unknown'
                                  ? 'Unknown pay cycle'
                                  : formatTimesheetDateRange(
                                      payCycle.startDateISO,
                                      payCycle.endDateISO,
                                    )}
                              </h3>
                              {payCycle.items.map((item) => (
                                <TimesheetCard
                                  key={item.daySequence.id}
                                  item={item}
                                  canEdit={canApprove}
                                  showClientName={showClientName}
                                  isEditing={
                                    editingDaySequenceId === item.daySequence.id
                                  }
                                  isSaving={
                                    savingDaySequenceId === item.daySequence.id
                                  }
                                  onStartEdit={() =>
                                    setEditingDaySequenceId(item.daySequence.id)
                                  }
                                  onCancelEdit={() =>
                                    setEditingDaySequenceId(null)
                                  }
                                  onSave={saveTimesheet}
                                />
                              ))}
                              {canApprove && (
                                <Button
                                  type="button"
                                  variant="primary"
                                  disabled={
                                    approvingCycleKey !== null ||
                                    savingDaySequenceId !== null ||
                                    payCycle.items.some(
                                      (item) =>
                                        item.daySequence.id ===
                                        editingDaySequenceId,
                                    )
                                  }
                                  onClick={() => setPayCycleToApprove(payCycle)}
                                  className="!rounded-lg !py-2 !text-sm !font-medium"
                                >
                                  Approve timesheets
                                </Button>
                              )}
                            </section>
                          ))}
                        </div>
                      </div>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ApproveTimesheetsModal
        isOpen={payCycleToApprove !== null}
        workerName={group?.workerName ?? ''}
        startDateISO={payCycleToApprove?.startDateISO ?? ''}
        endDateISO={payCycleToApprove?.endDateISO ?? ''}
        items={payCycleToApprove?.items ?? []}
        showClientName={showClientName}
        isApproving={
          payCycleToApprove?.key === approvingCycleKey &&
          approvingCycleKey !== null
        }
        onClose={() => setPayCycleToApprove(null)}
        onConfirm={() => {
          if (!payCycleToApprove) return;
          void onApproveCycle(
            payCycleToApprove.key,
            payCycleToApprove.items.map((item) => item.daySequence.id),
          ).then(() => setPayCycleToApprove(null));
        }}
      />
    </>
  );
};

export default TimesheetGroupDrawer;
